import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

const useHistoryQuery = () => {
  const history = useHistory();
  const location = useLocation();

  const value = useMemo(() => {
    const currentQuery = new URLSearchParams(location.search);
    const result = {};

    try {
      currentQuery.forEach((val, name) => {
        result[name] = JSON.parse(val);
      });
    } catch (ex) {
      console.warn(ex);
    }
    return result;
  }, [location]);

  const makeLink = useCallback(
    (values = {}, options = {}) => {
      const {
        replace = false,
        removeEmpty = true,
        pathname = history.location.pathname,
      } = options;

      const newQuery = new URLSearchParams(
        replace ? undefined : history.location.search,
      );
      Object.entries(values).forEach(([name, v]) => {
        const isEmptyString = typeof v === 'string' && v === '';
        const isNumber = typeof v === 'number' && Number.isFinite(v);
        const isEmptyArray = Array.isArray(v) && v.length === 0;
        const isObject = !!v && typeof v === 'object';
        const isEmptyObject = isObject && Object.keys(v).length === 0;
        const isEmpty =
          (!v && !isNumber) || isEmptyString || isEmptyArray || isEmptyObject;
        const newValue = JSON.stringify(v);
        const currentValue = newQuery.get(name);
        const hasEmptyKey = newQuery.has(name) && isEmpty;

        if (isEmpty) {
          newQuery.delete(name);
        } else if (newValue === currentValue) {
          return;
        }
        if (!hasEmptyKey) {
          if (removeEmpty && isEmpty) {
            return;
          }
          newQuery.set(name, newValue);
        }
      });
      const newQueryString = newQuery.toString();
      const result = [pathname, newQueryString].join(newQueryString ? '?' : '');
      return result;
    },
    [history],
  );

  const setValueLink = useCallback(
    (name, v, options = {}) => {
      return makeLink({ [name]: v }, options);
    },
    [makeLink],
  );

  const setValue = useCallback(
    (...args) => {
      const link = setValueLink(...args);
      history.push(link);
    },
    [setValueLink, history],
  );

  const setValues = useCallback(
    (...args) => {
      const link = makeLink(...args);
      history.push(link);
    },
    [makeLink, history],
  );

  const clear = useCallback(() => {
    const base = history.location.pathname;
    history.push(base);
  }, [history]);

  const remove = useCallback(
    (name) => {
      setValue(name, '');
    },
    [setValue],
  );

  return useMemo(
    () => ({
      value,
      setValue,
      setValues,
      clear,
      remove,
      setValueLink,
      makeLink,
    }),
    [value, setValue, setValueLink, clear, remove, setValues, makeLink],
  );
};

export default useHistoryQuery;
